import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import { getQuoteByListApi } from "features/quotation/api/quotationListApi";
import { TblQuoteBy } from "features/quotation/types/quotation";
import Notify from "config/notify";
import { DASH_FORMAT_DATE, SLASH_FORMAT_DATE } from "constants/format";
import { PunctureProtection } from "features/mst_option/types/option";
import { getPructureProtectionApi } from "features/mst_option/api/optionApi";
import { cloneDeep } from "lodash";
import { clearSessionData, getSessionData, setSessionData } from "util/session_action";
import { SESSION_EST_RESULT_KEYS, SESSION_KEY } from "constants/key";
import { createQuoteApi } from "features/car_model/api/carModelApi";
import { EstCreateRequest, EstQuoteDetailTire, NewQuoteMaker, PrcFeesData } from "features/car_model/types";

const PAGE_ROUTES = {
  CAR_TYPE: "/mst-car-model/selection",
  TIRE_SIZE: "/mst-car-tiresize/search",
  ITEM_CODE: "/mst-car-model/product-code",
} as const;

const defaultTireDetail: EstQuoteDetailTire = {
  tire_item_cd: "",
  maker_name: "",
  item_name: "",
  tire_loin_speed_mark: "",
  tire_label: "",
  quantity: 0,
  nomal_price: 0,
  fixed_discount: 0,
  special_discount: 0,
  discounted_sale_price: 0,
  wage_cost_price: 0,
  wage_cost_in_price: 0,
  puncture_protection_price: 0,
  puncture_protection_in_price: 0,
  cb_tire_price: 0,
  cb_wheel_price: 0,
  confirm_total_price: 0,
  remarks: "",
  input_remarks: "",
  checked: true,
};

const defaultEst: EstCreateRequest = {
  mode: "tire",
  customer_name: "",
  customer_tel_no: "",
  quote_by: "",
  exp_date: dayjs().add(1, "week").format(SLASH_FORMAT_DATE),
  brnd_label: "",
  brnd_name: "",
  modl_code: 0,
  modl_name: "",
  fmc_code: "",
  modl_period_s: "",
  modl_period_e: "",
  car_tiresize_id: 0,
  mmm_idx: 0,
  tire_puncture_flag: 0,
  replac_labor_cost: 0,
  waste_tire_cost: 0,
  rubber_stem_valve_cost: 0,
  nitrogen_inflation_cost: 0,
  hub_rust_prevention_cost: 0,
  waste_tire_cost_flag: 0,
  rubber_stem_valve_flag: 0,
  nitrogen_inflation_flag: 0,
  hub_rust_prevention_flag: 0,
  round_type: "0",
  tbl_quote_detail_tire: [],
};

const calculateCompensation = (
  totalPrice: number,
  punctureProtection: PunctureProtection[],
  punctureCompensation: number,
): number => {
  if (!punctureProtection || punctureCompensation === 0) return 0;

  const matchedProtection = punctureProtection.find(
    (item) =>
      item.coverage_amount_low <= totalPrice &&
      item.coverage_amount_high >= totalPrice &&
      item.puncture_protection_name.includes(`${punctureCompensation}本`),
  );
  console.log(
    "matchedProtection?.coverage_enrollment_fee",
    matchedProtection?.coverage_enrollment_fee,
    totalPrice,
  );
  return matchedProtection?.coverage_enrollment_fee || 0;
};

const calculateWageCost = (fee: PrcFeesData, tireNumber: number): number => {
  const costItems = [
    { value: fee.replac_labor_cost.value ?? 0, checked: true },
    { value: fee.waste_tire_cost.value ?? 0, checked: fee.waste_tire_cost.checked },
    { value: fee.rubber_stem_valve_cost.value ?? 0, checked: fee.rubber_stem_valve_cost.checked },
    { value: fee.nitrogen_inflation_cost.value ?? 0, checked: fee.nitrogen_inflation_cost.checked },
    {
      value: fee.hub_rust_prevention_cost.value ?? 0,
      checked: fee.hub_rust_prevention_cost.checked,
    },
  ];

  const totalCost = costItems.reduce((sum, item) => sum + item.value * Number(item.checked), 0);

  return Math.round(totalCost * tireNumber);
};

const procEstCalculate = (
  estResult: {
    cashback_period: number;
    puncture: string;
  },
  fee: PrcFeesData,
  tireNumber: number,
  tire: EstQuoteDetailTire,
  punctureProtection: PunctureProtection[],
): EstQuoteDetailTire => {
  const { cashback_period: cashbackPeriod, puncture } = estResult;
  const wage = calculateWageCost(fee, tireNumber);
  const wageCostInPrice = Math.round(wage + tire.discounted_sale_price);

  const punctureProtectionPrice = calculateCompensation(
    wageCostInPrice,
    punctureProtection,
    Number(puncture),
  );

  const punctureProtectionInPrice = wageCostInPrice + punctureProtectionPrice;
  const isCashbackPeriod2 = cashbackPeriod === 2;

  return {
    ...tire,
    wage_cost_price: wage,
    wage_cost_in_price: wageCostInPrice,
    puncture_protection_price: punctureProtectionPrice,
    puncture_protection_in_price: punctureProtectionInPrice,
    confirm_total_price: Math.round(
      wageCostInPrice + punctureProtectionPrice + (isCashbackPeriod2 ? tire.cb_tire_price : 0),
    ),
  };
};

/**
 * Custom hook for managing estimate results and calculations
 */
const useEstResult = () => {
  const [estResult, setEstResult] = useState<EstCreateRequest>(defaultEst);
  const [estPrcChecked, setEstPrcChecked] = useState<Record<keyof MstProcFeeTireItem, boolean>>(
    {} as any,
  );
  const [estCashback, setEstCashback] = useState<{
    cashback_period: number;
    puncture: string;
  }>({ cashback_period: 0, puncture: "0" });
  const [newQuoteMaker, setNewQuoteMaker] = useState<NewQuoteMaker | undefined>(undefined);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [quoteByList, setQuoteByList] = useState<{ label: string }[]>([]);
  const [punctureProtection, setPunctureProtection] = useState<PunctureProtection[]>([]);
  const navigate = useNavigate();

  const isFromCarType = useMemo(() => selectedPage === PAGE_ROUTES.CAR_TYPE, [selectedPage]);
  const isFromTireSize = useMemo(() => selectedPage === PAGE_ROUTES.TIRE_SIZE, [selectedPage]);
  const isFromItemCode = useMemo(() => selectedPage === PAGE_ROUTES.ITEM_CODE, [selectedPage]);

  const tireSizeText = useMemo(() => {
    if (isFromTireSize) {
      const quoteTires =
        getSessionData<
          {
            tire_width: number;
            tire_aspect_ratio: number;
            tire_rim_diam: number;
          }[]
        >(SESSION_EST_RESULT_KEYS.NEW_QUOTE_TIRE) || [];

      return quoteTires
        .map((item, i, arr) => {
          const prefix = arr.length > 1 ? (i === 0 ? "F " : "R ") : "";
          return `${prefix}${item.tire_width}/${item.tire_aspect_ratio}R${item.tire_rim_diam}`;
        })
        .join(" ");
    }
    return newQuoteMaker?.selectedTireSize?.car_tiresize ?? "";
  }, [selectedPage, newQuoteMaker]);

  const updateQuantity = useCallback((value: number) => {
    setEstResult((prev) => ({
      ...prev,
      tbl_quote_detail_tire: prev.tbl_quote_detail_tire.map((item) => ({
        ...item,
        quantity: value,
      })),
    }));
  }, []);

  const setEstResultByKey = useCallback((key: keyof EstCreateRequest, value: any) => {
    setEstResult((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setEstResultDetail = useCallback(
    (key: keyof EstQuoteDetailTire, index: number, value: any) => {
      setEstResult((prev) => {
        const updated = [...prev.tbl_quote_detail_tire];
        updated[index] = { ...updated[index], [key]: value };
        return { ...prev, tbl_quote_detail_tire: updated };
      });
    },
    [],
  );

  const parseInitialEstimate = useCallback((): EstCreateRequest => {
    let est = { ...defaultEst };
    est.quote_by = sessionStorage.getItem(SESSION_EST_RESULT_KEYS.QUOTE_BY) || "";

    const procFees = getSessionData<{
      proc_fees?: PrcFeesData;
      tbl_quote_detail_tire?: Partial<EstQuoteDetailTire>[];
      cashback?: {
        cashback_period: number;
        puncture: string;
      };
    }>(SESSION_KEY.SELECTED_TIRE);

    if (procFees) {
      const feeValues: Partial<MstProcFeeTireItem> = {};
      const checkedStates: Record<keyof MstProcFeeTireItem, boolean> = {} as any;

      if (procFees.proc_fees) {
        const defaultProcFee = procFees.proc_fees;
        Object.keys(defaultProcFee).forEach((key) => {
          const typedKey = key as keyof MstProcFeeTireItem;
          feeValues[typedKey] = Number(
            defaultProcFee[typedKey]?.value ??
              defaultEst[typedKey as keyof Omit<EstCreateRequest, "tbl_quote_detail_tire">],
          );
          checkedStates[typedKey] = defaultProcFee[typedKey]?.checked ?? false;
        });
      }

      est = { ...est, ...feeValues };

      if (procFees.tbl_quote_detail_tire) {
        const tireDetail = procFees.tbl_quote_detail_tire.map((item) => {
          const data: EstQuoteDetailTire = {
            ...defaultTireDetail,
            ...item,
            input_remarks: "", // Always empty on first enter; user types then submits
          };

          Object.keys(defaultTireDetail).forEach((key) => {
            const typedKey = key as keyof EstQuoteDetailTire;
            if (
              (data[typedKey] === null || data[typedKey] === undefined) &&
              defaultTireDetail[typedKey] !== undefined
            ) {
              (data as any)[typedKey] = defaultTireDetail[typedKey];
            }
          });

          return data;
        });
        est.tbl_quote_detail_tire = tireDetail;
      }

      est.tire_puncture_flag = Number(procFees.cashback?.puncture ?? 0);
      setEstPrcChecked(checkedStates);
      setEstCashback(procFees.cashback ?? { cashback_period: 0, puncture: "0" });
    }

    const carInfo = getSessionData<Partial<EstCreateRequest>>(
      SESSION_EST_RESULT_KEYS.SELECTED_CAR_INFO,
    );
    if (carInfo) {
      est = { ...est, ...carInfo };
    }

    const quoteMaker = getSessionData<NewQuoteMaker>(SESSION_EST_RESULT_KEYS.NEW_QUOTE_MAKER);
    setNewQuoteMaker(quoteMaker);

    if (quoteMaker?.selectedTireSize && quoteMaker?.selectedTire) {
      const {
        selectedTireSize,
        selectedTire,
        car_fmc: carFmc,
        brnd_label: brndLabel,
        brnd_name: brndName,
        modl_code: modlCode,
        modl_name: modlName,
        modl_period_s: modlPeriodS,
        modl_period_e: modlPeriodE,
      } = quoteMaker;

      est.mmm_idx = selectedTire.mmm_idx;
      est.car_tiresize_id = selectedTireSize.car_tiresize_id;
      est.fmc_code = carFmc;
      est.brnd_label = brndLabel;
      est.brnd_name = brndName;
      est.modl_code = modlCode;
      est.modl_name = modlName;
      est.modl_period_s = modlPeriodS;
      est.modl_period_e = modlPeriodE;
    }

    return est;
  }, []);

  const getQuoteBy = useCallback(async () => {
    try {
      const data = await getQuoteByListApi();
      if (data.list.length > 0) {
        const formattedList = data.list.map((item: TblQuoteBy) => ({ label: item.quote_by }));
        setQuoteByList(formattedList);
      }
    } catch (error) {
      // Silently handle error
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (estResult.tbl_quote_detail_tire.every((item) => item.checked === false)) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
        actionsPosition: "end",
      });
      return;
    }

    const shouldSendResult = cloneDeep(estResult);

    Object.keys(estPrcChecked).forEach((key) => {
      const typedKey = key as keyof MstProcFeeTireItem;
      if (!estPrcChecked[typedKey]) {
        shouldSendResult[typedKey] = 0;
      }
      shouldSendResult.waste_tire_cost_flag = !estPrcChecked.waste_tire_cost ? 0 : 1;
      shouldSendResult.rubber_stem_valve_flag = !estPrcChecked.rubber_stem_valve_cost ? 0 : 1;
      shouldSendResult.nitrogen_inflation_flag = !estPrcChecked.nitrogen_inflation_cost ? 0 : 1;
      shouldSendResult.hub_rust_prevention_flag = !estPrcChecked.hub_rust_prevention_cost ? 0 : 1;
    });

    // Map input_remarks to remarks for API submission
    shouldSendResult.tbl_quote_detail_tire = shouldSendResult.tbl_quote_detail_tire.map(
      (item) => ({
        ...item,
        remarks: item.input_remarks ?? "", // Use user input as remarks
        ...(estCashback.cashback_period !== 2 && {
          cb_tire_price: 0,
          cb_wheel_price: 0,
        }),
      }),
    );

    const result = await createQuoteApi({
      ...shouldSendResult,
      exp_date: dayjs(shouldSendResult.exp_date).format(DASH_FORMAT_DATE),
    });
    if (result?.quote_id) {
      clearSessionData(SESSION_EST_RESULT_KEYS);
      navigate(`/quotation/${result.quote_id}`);
    } else {
      console.error("Failed to create quote");
    }
  }, [estResult, navigate]);

  useEffect(() => {
    setEstResult(parseInitialEstimate());
    getQuoteBy();
    setSelectedPage(sessionStorage.getItem(SESSION_EST_RESULT_KEYS.SELECTED_PAGE));
  }, [parseInitialEstimate, getQuoteBy]);

  useEffect(() => {
    const fetchPunctureProtection = async () => {
      try {
        const response = await getPructureProtectionApi();
        const sortedProtection = response.list.sort(
          (a: { coverage_amount_low: number }, b: { coverage_amount_low: number }) =>
            a.coverage_amount_low - b.coverage_amount_low,
        );
        setPunctureProtection(sortedProtection);
      } catch (error) {
        console.error("Failed to fetch puncture protection:", error);
      }
    };
    fetchPunctureProtection();
  }, []);

  useEffect(() => {
    const procFees = getSessionData<{
      proc_fees?: PrcFeesData;
      tbl_quote_detail_tire?: Partial<EstQuoteDetailTire>[];
      cashback?: {
        cashback_period: number;
        puncture: string;
      };
    }>(SESSION_KEY.SELECTED_TIRE);

    if (!procFees) return;

    const updatedProcFees = { ...procFees };

    if (estCashback) {
      updatedProcFees.cashback = estCashback;
    }

    if (procFees.proc_fees) {
      Object.keys(estPrcChecked).forEach((key) => {
        if (updatedProcFees.proc_fees) {
          updatedProcFees.proc_fees[key as keyof PrcFeesData].checked =
            estPrcChecked[key as keyof MstProcFeeTireItem];
          updatedProcFees.proc_fees[key as keyof MstProcFeeTireItem].value =
            estResult[key as keyof MstProcFeeTireItem];
        }
      });
    }

    setSessionData(SESSION_KEY.SELECTED_TIRE, updatedProcFees);

    if (estCashback && estPrcChecked && procFees.proc_fees) {
      setEstResult((prev) => ({
        ...prev,
        tire_puncture_flag: Number(estCashback.puncture),
        tbl_quote_detail_tire: prev.tbl_quote_detail_tire.map((item) =>
          procEstCalculate(
            estCashback,
            procFees.proc_fees!,
            item.quantity,
            item,
            punctureProtection,
          ),
        ),
      }));
    }
  }, [
    estCashback,
    estPrcChecked,
    punctureProtection,
    estResult.replac_labor_cost,
    estResult.hub_rust_prevention_cost,
    estResult.nitrogen_inflation_cost,
    estResult.rubber_stem_valve_cost,
    estResult.waste_tire_cost,
  ]);

  return {
    estResult,
    setEstResult,
    setEstResultByKey,
    setEstResultDetail,
    handleSubmit,
    estPrcChecked,
    isFromCarType,
    isFromTireSize,
    isFromItemCode,
    newQuoteMaker,
    tireSizeText,
    estCashback,
    updateQuantity,
    setEstPrcChecked,
    quoteByList,
    setEstCashback,
  };
};

export default useEstResult;
